import get                  from 'lodash/get';
import { Connector }        from '@mathquis/modelx';
import { ConnectorResult }  from '@mathquis/modelx';
import { ConnectorResults } from '@mathquis/modelx';
import { Model }            from '@mathquis/modelx';
import axios                from 'axios';
import currentSession       from 'utils/currentSession';

export class ApiConnector extends Connector {
  public _currentSessionStore: any;
  public client: any;
  public violations: any;

  public destroy(model: Model, options = {}) {
    return this
      .request(model.path, {
        method: 'delete',
        ...options,
      })
      .then((response: any) => new ConnectorResult(model.attributes, response));
  }

  public fetch(model: Model, options = {}) {
    return this
      .request(model.path, {
        method: 'get',
        ...options,
      })
      .then((response: any) => new ConnectorResult(response.data, response));
  }

  public initialize() {
    this._currentSessionStore = currentSession;

    this.client = axios.create({
      ...this.options,
      headers: {
        ...this.options.headers,
        Accept: 'application/ld+json',
      },
      responseType: 'json',
    });

    this.client.interceptors.request.use((config: any) => {
      let tokenCheck = Promise.resolve();

      if (!this._currentSessionStore) {
        console.error('Property currentSessionStore is required', this._currentSessionStore);
      }

      if (this._currentSessionStore.isExpired) {
        tokenCheck = tokenCheck.then(() => this._currentSessionStore.refresh());
      }

      return tokenCheck.then(() => {
        config.headers.Authorization = `Bearer ${this._currentSessionStore.token}`;
        return config;
      });
    });
  }

  public list(collection: any, options: any = {}) {
    const params = { ...options.filters };

    params.all = options.limit || options.offset ? undefined : 1;
    if (!params.all) {
      params.length = Math.max(1, options.limit);
      params.page = Math.floor(options.offset / Math.max(1, params.length)) + 1;
    }

    if (options.sort) {
      params.o = options.sort.o;
      params.w = options.sort.w;
    }

    return this
      .request(collection.path, {
        ...options,
        method: 'get',
        params,
      })
      .then((response: any) => new ConnectorResults(response.data, response));
  }

  public onRequestError(err: any) {
    console.error("API Error on path:", err.config?.url, err); // to have more details in sentry

    if (get(err, 'request.status', 0) === 401) {
      this._currentSessionStore.logout();
    }

    throw err; // to stop the chain
  }

  public request(path: string, options = {}) {
    if (!this.client) {
      this.initialize();
    }

    return this.client
      .request({
        url: path.replace(/\/$/, ''),
        ...options,
      })
      .catch((err: any) => this.onRequestError(err));
  }

  public save(model: Model, options: any = {}) {
    return this
      .request(model.path, {
        data: options.patchAttributes || model.untransformedAttributes,
        method: model.id ? 'put' : 'post',
        ...options,
      })
      .then((response: any) => {
        this.violations = [];
        return new ConnectorResult(response.data, response);
      });
  }

  public setCurrentSessionStore(store: any) {
    this._currentSessionStore = store;
  }
}
